import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/admin-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Plus, X, Pencil, Loader2, GripVertical } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface Hint {
  id: number;
  content: string;
  price: number;
  puzzleId: number;
}

interface Puzzle {
  id: number;
  title: string;
  description: string;
  bitcoinAddress: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  hints: Hint[];
}

const puzzleFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  bitcoinAddress: z.string().min(26, "Invalid Bitcoin address").max(35, "Invalid Bitcoin address"),
  sortOrder: z.number().default(0),
});

const hintFormSchema = z.object({
  content: z.string().min(1, "Hint content is required"),
  price: z.number().min(1, "Price must be at least 1"),
  hintId: z.number().optional(),
});

type PuzzleFormData = z.infer<typeof puzzleFormSchema>;
type HintFormData = z.infer<typeof hintFormSchema>;

function PuzzleForm({
  defaultValues,
  onSubmit,
  isSubmitting
}: {
  defaultValues?: PuzzleFormData;
  onSubmit: (data: PuzzleFormData) => void;
  isSubmitting: boolean;
}) {
  const form = useForm<PuzzleFormData>({
    resolver: zodResolver(puzzleFormSchema),
    defaultValues: defaultValues || {
      title: "",
      description: "",
      bitcoinAddress: "",
      sortOrder: 0,
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input {...field} className="bg-zinc-800 border-zinc-700" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea {...field} className="bg-zinc-800 border-zinc-700 min-h-[100px]" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="bitcoinAddress"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Bitcoin Address</FormLabel>
              <FormControl>
                <Input {...field} className="bg-zinc-800 border-zinc-700 font-mono" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {defaultValues ? "Update Puzzle" : "Add Puzzle"}
        </Button>
      </form>
    </Form>
  );
}

function HintForm({
  puzzleId,
  onCancel,
  defaultValues
}: {
  puzzleId: number;
  onCancel: () => void;
  defaultValues?: Partial<HintFormData>;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const form = useForm<HintFormData>({
    resolver: zodResolver(hintFormSchema),
    defaultValues: defaultValues || {
      content: "",
      price: 0,
    },
  });

  const addHint = useMutation({
    mutationFn: async ({ content, price }: HintFormData) => {
      const res = await fetch(`/api/admin/puzzles/${puzzleId}/hints`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, price }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/puzzles"] });
      toast({ title: "Hint added successfully" });
      form.reset();
      onCancel();
    },
    onError: (error) => {
      toast({
        title: "Error adding hint",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateHint = useMutation({
    mutationFn: async ({ content, price, hintId }: HintFormData) => {
      if (!hintId) throw new Error("Hint ID is required for update");

      const res = await fetch(`/api/admin/hints/${hintId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content, price }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/puzzles"] });
      toast({ title: "Hint updated successfully" });
      onCancel();
    },
    onError: (error) => {
      toast({
        title: "Error updating hint",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: HintFormData) => {
    if (defaultValues?.hintId) {
      updateHint.mutate({ ...data, hintId: defaultValues.hintId });
    } else {
      addHint.mutate(data);
    }
  };

  const isSubmitting = defaultValues?.hintId ? updateHint.isPending : addHint.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="content"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Content</FormLabel>
              <FormControl>
                <Input {...field} className="bg-zinc-800 border-zinc-700" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="price"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Price (cacatais)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  {...field}
                  onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  className="bg-zinc-800 border-zinc-700"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {defaultValues?.hintId ? "Update Hint" : "Add Hint"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function SortableTableRow({ puzzle, children }: { puzzle: Puzzle; children: React.ReactNode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: puzzle.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell {...attributes} {...listeners}>
        <Button variant="ghost" size="icon">
          <GripVertical className="h-4 w-4" />
        </Button>
      </TableCell>
      {children}
    </TableRow>
  );
}

export default function AdminPuzzlesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingPuzzle, setEditingPuzzle] = useState<Puzzle | null>(null);
  const [addingHintForPuzzle, setAddingHintForPuzzle] = useState<number | null>(null);
  const [editingHint, setEditingHint] = useState<Hint | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: puzzles, isLoading } = useQuery<Puzzle[]>({
    queryKey: ["/api/admin/puzzles"],
  });

  const addPuzzle = useMutation({
    mutationFn: async (puzzle: PuzzleFormData) => {
      const res = await fetch("/api/admin/puzzles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(puzzle),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/puzzles"] });
      toast({ title: "Puzzle added successfully" });
      setIsAddDialogOpen(false);
    },
    onError: (error) => {
      toast({
        title: "Error adding puzzle",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePuzzle = useMutation({
    mutationFn: async (data: PuzzleFormData & { id: number }) => {
      const { id, ...puzzle } = data;
      const res = await fetch(`/api/admin/puzzles/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(puzzle),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/puzzles"] });
      toast({ title: "Puzzle updated successfully" });
      setEditingPuzzle(null);
    },
    onError: (error) => {
      toast({
        title: "Error updating puzzle",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteHint = useMutation({
    mutationFn: async (hintId: number) => {
      const res = await fetch(`/api/admin/hints/${hintId}`, {
        method: "DELETE",
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/puzzles"] });
      toast({ title: "Hint deleted successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error deleting hint",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateSortOrder = useMutation({
    mutationFn: async ({ id, sortOrder }: { id: number; sortOrder: number }) => {
      const res = await fetch(`/api/admin/puzzles/${id}/sort-order`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sortOrder }),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/puzzles"] });
    },
    onError: (error) => {
      toast({
        title: "Error updating puzzle order",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = puzzles!.findIndex((p) => p.id === active.id);
    const newIndex = puzzles!.findIndex((p) => p.id === over.id);

    const newPuzzles = arrayMove(puzzles!, oldIndex, newIndex);

    newPuzzles.forEach((puzzle, index) => {
      updateSortOrder.mutate({ id: puzzle.id, sortOrder: index });
    });
  };

  return (
    <AdminLayout>
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle>Puzzles</CardTitle>
          <CardDescription>Manage Bitcoin puzzles (drag to reorder)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Add Puzzle Button and Dialog */}
            <Button onClick={() => setIsAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add New Puzzle
            </Button>

            <Dialog 
              open={isAddDialogOpen} 
              onOpenChange={(open) => setIsAddDialogOpen(open)}
            >
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle>Add New Puzzle</DialogTitle>
                </DialogHeader>
                <PuzzleForm 
                  onSubmit={(data) => {
                    addPuzzle.mutate(data);
                    setIsAddDialogOpen(false);
                  }}
                  isSubmitting={addPuzzle.isPending}
                />
              </DialogContent>
            </Dialog>

            {isLoading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead></TableHead>
                      <TableHead>Title</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Bitcoin Address</TableHead>
                      <TableHead>Hints</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <SortableContext
                    items={puzzles?.map(p => p.id) || []}
                    strategy={verticalListSortingStrategy}
                  >
                    <TableBody>
                      {puzzles?.map((puzzle) => (
                        <SortableTableRow key={puzzle.id} puzzle={puzzle}>
                          <TableCell className="font-medium">{puzzle.title}</TableCell>
                          <TableCell className="max-w-md truncate">{puzzle.description}</TableCell>
                          <TableCell>
                            <code className="bg-zinc-800 px-2 py-1 rounded text-xs">
                              {puzzle.bitcoinAddress}
                            </code>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-2">
                              {puzzle.hints?.map((hint) => (
                                <div key={hint.id} className="flex items-center gap-2 bg-zinc-800 p-2 rounded">
                                  <span className="flex-1 text-sm">{hint.content}</span>
                                  <span className="text-xs text-zinc-400">{hint.price} cacatais</span>
                                  <div className="flex gap-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setEditingHint(hint)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => deleteHint.mutate(hint.id)}
                                    >
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                              {editingHint?.puzzleId === puzzle.id ? (
                                <div className="bg-zinc-800 p-3 rounded">
                                  <HintForm
                                    puzzleId={puzzle.id}
                                    defaultValues={{
                                      content: editingHint.content,
                                      price: editingHint.price,
                                      hintId: editingHint.id,
                                    }}
                                    onCancel={() => setEditingHint(null)}
                                  />
                                </div>
                              ) : addingHintForPuzzle === puzzle.id ? (
                                <div className="bg-zinc-800 p-3 rounded">
                                  <HintForm
                                    puzzleId={puzzle.id}
                                    onCancel={() => setAddingHintForPuzzle(null)}
                                  />
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => setAddingHintForPuzzle(puzzle.id)}
                                >
                                  <Plus className="mr-2 h-3 w-3" />
                                  Add Hint
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => setEditingPuzzle(puzzle)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </SortableTableRow>
                      ))}
                    </TableBody>
                  </SortableContext>
                </Table>
              </DndContext>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Edit Puzzle Dialog */}
      <Dialog 
        open={editingPuzzle !== null}
        onOpenChange={(open) => !open && setEditingPuzzle(null)}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle>Edit Puzzle</DialogTitle>
          </DialogHeader>
          {editingPuzzle && (
            <PuzzleForm
              defaultValues={editingPuzzle}
              onSubmit={(data) => updatePuzzle.mutate({ ...data, id: editingPuzzle.id })}
              isSubmitting={updatePuzzle.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}